import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  IconButton,
  Backdrop,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import Loader from "components/loader";
import useAPI from "hooks/useAPI";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";

const ManufacturerFormPopup = ({
  open,
  onClose,
  onSuccess,
  manufacturer = null,
  mode = "add",
  showAlert = null,
}) => {
  const { t } = useTranslation();
  const { api } = useAPI();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
  });
  const [errors, setErrors] = useState({});

  const isEditMode = mode === "edit" && manufacturer;

  // Populate form data when editing
  useEffect(() => {
    if (isEditMode) {
      setFormData({
        name: manufacturer.name || "",
      });
    } else {
      setFormData({
        name: "",
      });
    }
    setErrors({});
  }, [isEditMode, manufacturer]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Manufacturer name is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      let response;
      const payload = {
        name: formData.name.trim(),
      };

      if (isEditMode) {
        response = await api.put(`/manufacturers/${manufacturer.id}/`, payload);
        showAlert("success", "Manufacturer updated successfully!");
      } else {
        response = await api.post("/manufacturers/", payload);
        showAlert("success", "Manufacturer created successfully!");
      }

      // Reset form
      setFormData({ name: "" });
      setErrors({});

      // Close popup and refresh data
      onClose();
      if (onSuccess) {
        onSuccess(response.data);
      }
    } catch (error) {
      console.error(`Error ${isEditMode ? "updating" : "creating"} manufacturer:`, error);

      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        showAlert(
          "error",
          `Failed to ${isEditMode ? "update" : "create"} manufacturer. Please try again.`
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({ name: "" });
      setErrors({});
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          position: "relative",
        },
      }}
    >
      <Backdrop
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(255, 255, 255, 0.8)",
          zIndex: 1,
          borderRadius: 2,
        }}
        open={loading}
      >
        <Loader color="inherit" />
      </Backdrop>

      <DialogTitle sx={{ pb: 1 }}>
        <SoftBox display="flex" justifyContent="space-between" alignItems="center">
          <SoftTypography variant="h5" fontWeight="bold">
            {isEditMode ? "Edit Manufacturer" : "Add New Manufacturer"}
          </SoftTypography>
          <IconButton onClick={handleClose} disabled={loading}>
            <Close />
          </IconButton>
        </SoftBox>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <SoftBox mb={2}>
                <SoftTypography variant="caption" fontWeight="bold" color="text">
                  Manufacturer Name *
                </SoftTypography>
                <SoftInput
                  name="name"
                  placeholder="Enter manufacturer name"
                  value={formData.name}
                  onChange={handleInputChange}
                  error={!!errors.name}
                  fullWidth
                  disabled={loading}
                />
                {errors.name && (
                  <SoftTypography
                    variant="caption"
                    color="error"
                    sx={{ mt: 0.5, display: "block" }}
                  >
                    {errors.name}
                  </SoftTypography>
                )}
              </SoftBox>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 1 }}>
          <SoftButton variant="outlined" color="secondary" onClick={handleClose} disabled={loading}>
            Cancel
          </SoftButton>
          <SoftButton
            type="submit"
            variant="contained"
            color="dark"
            disabled={loading || !formData.name.trim()}
          >
            {loading
              ? isEditMode
                ? "Updating..."
                : "Creating..."
              : isEditMode
              ? "Update Manufacturer"
              : "Create Manufacturer"}
          </SoftButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

ManufacturerFormPopup.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  manufacturer: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
  }),
  mode: PropTypes.oneOf(["add", "edit"]),
  showAlert: PropTypes.func,
};

ManufacturerFormPopup.defaultProps = {
  onSuccess: null,
  manufacturer: null,
  mode: "add",
  showAlert: null,
};

export default ManufacturerFormPopup;
